import { saveAs } from "file-saver";
import JSZip from "jszip";
import { getImageDimensionsByImageUrl } from "../DesignEditor/views/DesignEditor/utils/smartImageUtils.ts";
import { AspectRatio } from "../components/common/DropDowns/StaticDropdown.tsx";
import { ZERO_BYTE_IMAGE_ERROR_MESSAGE } from "../constants/ImageToImageConstants.ts";
import LinkConstants from "../constants/LinkConstants.ts";
import { BRIA_USER_ASSETS } from "../constants/common.ts";
import { IframePostMessageActionTypes, IframePostMessageItemTypes } from "../models/new-iframe.ts";
import iframeStore from "../pages/IframeNew/iframe-store.tsx";
import ImagesService from "../services/ImagesService.ts";
import QueryService from "./QueryService.ts";
import { getFileExtensionFromUrl, getSafeFileName } from "./index.ts";

export type AsyncProps = {
	maxAttempts?: number;
	sleepSecs?: number;
	abortController?: AbortController | null;
	isV2?: boolean;
	statusUrl?: string;
	apiToken?: string; // API token for V2 polling
	onPollingChange?: (polling: boolean) => void;
	onProgress?: (info: { tries: number }) => void;
};

type ImageUtils = {
	exportImage: (
		src: string,
		fileName: string,
		resolution?: number,
		action?: "download" | "save",
		fileType?: string,
	) => Promise<void>;
	exportZipImages: (
		images: {
			fileName: string;
			imageUrl: string;
		}[],
		fileName: string,
		action?: "download" | "save",
		additionalFiles?: File[],
		fileType?: string,
	) => Promise<void>;
	getFileExtension: (blob: Blob) => string;
	isS3Uri: (uri: string) => boolean;
	convertS3UriToHttpsUrl: (s3Uri: string) => string;
	convertS3UriToHttpsCloudFrontUrl: (s3Uri: string) => string;
	getBlob: (src: string) => Promise<Blob>;
	isBlob: (url: string) => boolean;
	getBase64: (blob: Blob) => Promise<string | ArrayBuffer | null>;
	pullUntilAvailable: (fileUrl: string, asyncProps?: AsyncProps) => Promise<void>;
	pullUntilAvailableV2: (fileUrl: string, asyncProps?: AsyncProps) => Promise<any>;
	checkIfImageZeroByte: (url: string) => Promise<boolean>;
	getAspectRatioFromBase64: (base64: string) => Promise<number>;
	getAspectRatioFromImageUrl: (imageUrl: string) => Promise<string>;
	getAspectRatioFromFile: (file: File) => Promise<AspectRatio>;
	urlToBase64: (url: string) => Promise<string>;
	loadImageFromBlob: (blob: Blob) => Promise<HTMLImageElement>;
	processWhitePixels: (
		image: HTMLImageElement,
		fillColor: { r: number; g: number; b: number; a: number },
	) => HTMLCanvasElement;
	generateVideoThumbnail: (source: File | string) => Promise<string>;
};

const useImageUtils = (): ImageUtils => {
	const exportImage = async (
		url: string,
		fileName: string,
		resolution: number = 1,
		action: "download" | "save" = "download",
		fileType?: string,
	) => {
		const scaledUrl = resolution === 1 ? url : await new ImagesService().increaseResolution(url, resolution);
		if (action === "download") {
			const blob = await getBlob(scaledUrl);
			const extension = fileType?.toLowerCase() || getFileExtension(blob);
			const fileNameWithExtension = `${getSafeFileName(fileName)}.${extension}`;
			saveAs(blob, fileNameWithExtension);

			iframeStore.sendExportPostMessage(IframePostMessageActionTypes.Download, IframePostMessageItemTypes.Image, [
				{ type: IframePostMessageItemTypes.Image, src: scaledUrl, fileName: fileNameWithExtension },
			]);
		} else if (action === "save") {
			const extension = fileType?.toLowerCase() || getFileExtensionFromUrl(scaledUrl);
			const fileNameWithExtension = `${getSafeFileName(fileName)}.${extension}`;
			iframeStore.sendExportPostMessage(IframePostMessageActionTypes.Save, IframePostMessageItemTypes.Image, [
				{ type: IframePostMessageItemTypes.Image, src: scaledUrl, fileName: fileNameWithExtension },
			]);
		}
	};

	const exportZipImages = async (
		images: {
			fileName: string;
			imageUrl: string;
		}[],
		zipName: string,
		action: "download" | "save" = "download",
		additionalFiles?: File[],
		fileType?: string,
	) => {
		if (images.length) {
			const zip = new JSZip();
			const getExtension = (image: { imageUrl: string }, blob: Blob) =>
				fileType?.toLowerCase() || getFileExtension(blob) || getFileExtensionFromUrl(image.imageUrl);
			const withIndex = (name: string, index: number) => (images.length > 1 ? `${name}_${index}` : name);

			try {
				await Promise.all([
					...images.map(async (image, _index) => {
						try {
							const blob = await getBlob(image.imageUrl);
							const fullFileName = `${withIndex(getSafeFileName(image.fileName), _index)}.${getExtension(
								image,
								blob,
							)}`;
							const base64 = ((await getBase64(blob)) as string).split(",")[1];
							zip.file(fullFileName, base64, { base64: true });
						} catch (err) {
							console.error(`Error processing ${image.fileName}:`, err);
						}
					}),
					...(additionalFiles?.map(async (file) => {
						zip.file(file.name, file);
					}) || []),
				]);
				if (action === "download") {
					zip.generateAsync({ type: "blob" }).then((content) => {
						saveAs(content, `${zipName}.zip`);
					});
					iframeStore.sendExportPostMessage(
						IframePostMessageActionTypes.Download,
						IframePostMessageItemTypes.Image,
						images.map((image, index) => ({
							type: IframePostMessageItemTypes.Image,
							src: image.imageUrl,
							fileName: `${withIndex(getSafeFileName(image.fileName), index)}.${
								fileType?.toLowerCase() || getFileExtensionFromUrl(image.imageUrl)
							}`,
						})),
					);
				} else if (action === "save") {
					iframeStore.sendExportPostMessage(
						IframePostMessageActionTypes.Save,
						IframePostMessageItemTypes.Image,
						images.map((image, index) => ({
							type: IframePostMessageItemTypes.Image,
							src: image.imageUrl,
							fileName: `${withIndex(getSafeFileName(image.fileName), index)}.${
								fileType?.toLowerCase() || getFileExtensionFromUrl(image.imageUrl)
							}`,
						})),
					);
				}
			} catch (err) {
				console.error("Error creating zip file:", err);
			}
		}
	};

	const getFileExtension = (blob: Blob): string => {
		const parts = blob.type.split("/");
		return parts[parts.length - 1].replace("octet-stream", "png").replace("svg+xml", "svg");
	};

	const isS3Uri = (uri: string): boolean => {
		return uri?.startsWith("s3://");
	};

	const convertS3UriToHttpsUrl = (s3Uri: string): string => {
		const parts = s3Uri.split("/");
		const bucketName = parts[2];
		const filePath = parts.slice(3).join("/");
		const region = "us-east-1";

		return `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;
	};

	const getBase64 = (blob: Blob): Promise<string | ArrayBuffer | null> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(blob);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});
	};

	const getAspectRatioFromBase64 = (base64: string): Promise<number> => {
		return new Promise<number>((resolve, reject) => {
			const img = new Image();

			img.onload = () => {
				const aspectRatio = img.width / img.height;
				resolve(aspectRatio);
			};

			img.onerror = reject;
			img.src = base64;
		});
	};

	const getAspectRatioFromFile = (file: File): Promise<AspectRatio> => {
		const getGCD = (a: number, b: number): number => {
			while (b !== 0) {
				[a, b] = [b, a % b];
			}
			return a;
		};

		return new Promise((resolve, reject) => {
			const img = new Image();

			img.onload = () => {
				const { width, height } = img;
				const divisor = getGCD(width, height);
				const simplifiedWidth = Math.round(width / divisor);
				const simplifiedHeight = Math.round(height / divisor);

				resolve(`${simplifiedWidth}:${simplifiedHeight}` as AspectRatio);
			};

			img.onerror = reject;
			img.src = URL.createObjectURL(file);
		});
	};

	const getBlob = async (url: string) => {
		const response = await fetch(url);
		const blob = await response.blob();
		return blob;
	};

	const isBlob = (url: string) => {
		return url.startsWith("blob:");
	};

	async function pullingFetch(url: string) {
		try {
			const response = await fetch(url, {
				headers: {
					Range: "bytes=0-0",
				},
			});
			if (response.status === 200 || response.status === 206) return true;
			else if (response.status === 416) {
				throw new Error(ZERO_BYTE_IMAGE_ERROR_MESSAGE);
			}
			// Not functioning yet, wait for engine deployment to check implementation
			// if (response.status === 206) {
			// 	const sizeMatch = response.headers.get("Content-Range")?.match(/\/(\d+)$/);
			// 	return sizeMatch && parseInt(sizeMatch[1]) > 0;
			// }
		} catch (e: any) {
			if (e.message === ZERO_BYTE_IMAGE_ERROR_MESSAGE) {
				throw e;
			}
			return false;
		}
		return false;
	}

	const checkIfImageZeroByte = async (url: string) => {
		try {
			const response = await fetch(url);
			const fileSize = (await response.blob()).size;
			return fileSize !== undefined && fileSize === 0;
		} catch (e) {
			return false;
		}
	};

	const pullUntilAvailable = async (
		fileUrl: string,
		{ maxAttempts = 120, sleepSecs = 1, abortController }: AsyncProps = {},
	): Promise<void> => {
		if (!fileUrl) throw new Error("Invalid input");
		for (let tries = 0; tries < maxAttempts; tries++) {
			if (abortController?.signal.aborted) throw new Error("Aborted");
			if (await pullingFetch(fileUrl)) return;
			await new Promise((resolve) => setTimeout(resolve, sleepSecs * 1000));
		}
		throw new Error("Max attempts reached");
	};

	const pullUntilAvailableV2 = async (
		fileUrl: string,
		{
			maxAttempts = 1200,
			sleepSecs = 1,
			abortController,
			isV2 = false,
			statusUrl,
			onPollingChange,
			onProgress,
		}: AsyncProps = {},
	): Promise<any> => {
		if (!fileUrl && !isV2) throw new Error("Invalid input");

		if (isV2 && statusUrl) {
			return await pollV2Status(statusUrl, {
				maxAttempts,
				sleepSecs,
				abortController,
				onPollingChange,
				onProgress,
			});
		}
		throw new Error("Max attempts reached");
	};

	const pollV2Status = async (
		statusUrl: string,
		{ maxAttempts = 360, sleepSecs = 2, abortController, onPollingChange, onProgress }: AsyncProps = {},
	): Promise<any> => {
		if (!statusUrl) throw new Error("Invalid status URL");

		onPollingChange?.(true);
		try {
			for (let tries = 0; tries < maxAttempts; tries++) {
				if (abortController?.signal.aborted) throw new Error("Aborted");

				const statusId = statusUrl.split("/").pop();
				if (!statusId) throw new Error("Invalid status ID");

				const queryService = new QueryService("");
				const data = await queryService.get(`/status_check/${statusId}`, { signal: abortController?.signal });

				onProgress?.({ tries });

				const status = data?.status?.toUpperCase();
				if (status === "COMPLETED") return data.result;
				if (status === "ERROR" || status === "FAILED") {
					const errorMessage = data?.error?.message || "";
					const errorDetails = data?.error?.details || "";

					// Check if this is a content moderation error
					const isContentModeration =
						errorMessage.toLowerCase().includes("content moderation") ||
						errorDetails.toLowerCase().includes("content moderation");

					if (isContentModeration) {
						// Throw a specific error for content moderation
						const error = new Error("CONTENT_MODERATION_ERROR");
						(error as any).isContentModeration = true;
						throw error;
					}

					throw new Error(errorMessage || "Image generation failed");
				}

				// interruptible sleep
				await new Promise((resolve, reject) => {
					const id = setTimeout(resolve, sleepSecs * 1000);
					const abortHandler = () => {
						clearTimeout(id);
						reject(new Error("Aborted"));
					};
					abortController?.signal.addEventListener("abort", abortHandler, { once: true });
				});
			}
			throw new Error("Max attempts reached");
		} finally {
			onPollingChange?.(false);
		}
	};

	const convertS3UriToHttpsCloudFrontUrl = (s3Uri: string): string => {
		const parts = s3Uri.split("/");
		const bucketName = parts[2];
		let newHost = LinkConstants.BRIA_ORG_ASSETS_CLOUDFRONT;
		if (bucketName == BRIA_USER_ASSETS) {
			newHost = LinkConstants.BRIA_USER_ASSETS_CLOUDFRONT;
		}
		const filePath = parts.slice(3).join("/");

		return `${newHost}${filePath}`;
	};

	const getAspectRatioFromImageUrl = async (imageUrl: string): Promise<string> => {
		const imageDimensions = await getImageDimensionsByImageUrl(imageUrl);
		const { width, height } = imageDimensions;

		if (width === 0 || height === 0) {
			throw new Error("Invalid image dimensions");
		}

		// Calculate the GCD
		const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

		const divisor = gcd(width, height);

		// Simplify the width and height to get the aspect ratio
		const aspectWidth = width / divisor;
		const aspectHeight = height / divisor;

		return `${aspectWidth}:${aspectHeight}`;
	};

	async function urlToBase64(url: string): Promise<string> {
		const response = await fetch(url);
		const blob = await response.blob();
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				if (typeof reader.result === "string") {
					resolve(reader.result);
				} else {
					reject(new Error("Failed to convert blob to Base64"));
				}
			};
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}

	const loadImageFromBlob = (blob: Blob): Promise<HTMLImageElement> => {
		const imageUrl = URL.createObjectURL(blob);
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = "anonymous";
			img.onload = () => resolve(img);
			img.onerror = () => reject(new Error("Failed to load image"));
			img.src = imageUrl;
		});
	};

	const processWhitePixels = (
		image: HTMLImageElement,
		fillColor: { r: number; g: number; b: number; a: number },
	): HTMLCanvasElement => {
		const canvas = document.createElement("canvas");
		canvas.width = image.width;
		canvas.height = image.height;
		const ctx = canvas.getContext("2d");
		if (!ctx) throw new Error("Failed to get canvas context");

		ctx.drawImage(image, 0, 0);
		const imageData = ctx.getImageData(0, 0, image.width, image.height);
		const data = imageData.data;

		for (let i = 0; i < data.length; i += 4) {
			const [r, g, b, a] = data.slice(i, i + 4);
			if (r > 200 && g > 200 && b > 200 && a > 0) {
				data[i] = fillColor.r;
				data[i + 1] = fillColor.g;
				data[i + 2] = fillColor.b;
				data[i + 3] = fillColor.a;
			} else {
				data[i + 3] = 0;
			}
		}

		ctx.putImageData(imageData, 0, 0);
		return canvas;
	};

	const generateVideoThumbnail = (source: File | string): Promise<string> => {
		return new Promise((resolve, reject) => {
			const video = document.createElement("video");
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");

			if (!ctx) {
				reject(new Error("Could not get canvas context"));
				return;
			}

			const isUrl = typeof source === "string";
			let objectUrl: string | null = null;

			video.preload = "metadata";
			if (isUrl) {
				video.crossOrigin = "anonymous";
				video.src = source;
			} else {
				objectUrl = URL.createObjectURL(source);
				video.src = objectUrl;
			}

			const cleanup = () => {
				if (objectUrl) {
					URL.revokeObjectURL(objectUrl);
				}
			};

			video.onloadedmetadata = () => {
				video.currentTime = Math.min(1, video.duration / 2);
			};

			video.onseeked = () => {
				canvas.width = video.videoWidth;
				canvas.height = video.videoHeight;
				ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
				canvas.toBlob((blob) => {
					cleanup();
					if (blob) {
						const thumbnailUrl = URL.createObjectURL(blob);
						resolve(thumbnailUrl);
					} else {
						reject(new Error("Failed to generate thumbnail"));
					}
				}, "image/png");
			};

			video.onerror = () => {
				cleanup();
				reject(new Error("Failed to load video"));
			};
		});
	};

	return {
		exportImage,
		exportZipImages,
		getFileExtension,
		isS3Uri,
		convertS3UriToHttpsUrl,
		convertS3UriToHttpsCloudFrontUrl,
		getBlob,
		isBlob,
		pullUntilAvailable,
		pullUntilAvailableV2,
		checkIfImageZeroByte,
		getBase64,
		getAspectRatioFromBase64,
		getAspectRatioFromImageUrl,
		getAspectRatioFromFile,
		urlToBase64,
		loadImageFromBlob,
		processWhitePixels,
		generateVideoThumbnail,
	};
};

export default useImageUtils;

export const supportedImageFileTypes = ["png", "jpg", "jpeg", "webp"];
